import { Injectable, signal, computed } from '@angular/core';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: number;
  type: ToastType;
  message: string;
  durationMs: number;
}

const DEFAULT_DURATION: Record<ToastType, number> = {
  success: 5000,
  info: 5000,
  warning: 7000,
  error: 8000,
};

const MAX_VISIBLE = 4;

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly _toasts = signal<Toast[]>([]);
  private nextId = 1;
  private timers = new Map<number, ReturnType<typeof setTimeout>>();

  readonly toasts = computed(() => this._toasts());

  show(input: { type: ToastType; message: string; durationMs?: number }): number {
    const id = this.nextId++;
    const message = (input.message ?? '').trim();
    if (!message) return id;

    const toast: Toast = {
      id,
      type: input.type,
      message,
      durationMs: input.durationMs ?? DEFAULT_DURATION[input.type],
    };

    this._toasts.update(list => {
      const next = [...list, toast];
      if (next.length > MAX_VISIBLE) {
        const removed = next.splice(0, next.length - MAX_VISIBLE);
        removed.forEach(t => this.clearTimer(t.id));
      }
      return next;
    });

    if (toast.durationMs > 0) {
      const handle = setTimeout(() => this.dismiss(id), toast.durationMs);
      this.timers.set(id, handle);
    }
    return id;
  }

  success(message: string, durationMs?: number) { return this.show({ type: 'success', message, durationMs }); }
  error(message: string, durationMs?: number)   { return this.show({ type: 'error',   message, durationMs }); }
  info(message: string, durationMs?: number)    { return this.show({ type: 'info',    message, durationMs }); }
  warning(message: string, durationMs?: number) { return this.show({ type: 'warning', message, durationMs }); }

  dismiss(id: number): void {
    this.clearTimer(id);
    this._toasts.update(list => list.filter(t => t.id !== id));
  }

  clearAll(): void {
    this.timers.forEach(handle => clearTimeout(handle));
    this.timers.clear();
    this._toasts.set([]);
  }

  private clearTimer(id: number): void {
    const handle = this.timers.get(id);
    if (handle) {
      clearTimeout(handle);
      this.timers.delete(id);
    }
  }
}
